<script setup lang="ts">
import { computed } from 'vue'

import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()

/** 有选中图层 id 即可使用工具（与 List 选中态一致，且保持响应式） */
const canUseTransformTools = computed(
  () => Boolean(store.selectedLayerId) && store.animationLayers.length > 0,
)
</script>

<template>
  <div class="dock" role="toolbar" :aria-label="store.tUi('floatingTools.toolbarAria')">
    <button
      type="button"
      class="ico ico-tool ico-move"
      :class="{ on: store.viewportTransformTool === 'move' }"
      :aria-pressed="store.viewportTransformTool === 'move'"
      :aria-label="store.tUi('floatingTools.moveAria')"
      :title="store.tUi('floatingTools.moveTitle')"
      :disabled="!canUseTransformTools"
      @click.stop="store.setViewportTransformTool('move')"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
        <path
          stroke="currentColor"
          stroke-width="1.55"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8M18 8a2 2 0 1 1 4 0v5a8 8 0 0 1-8 8h-2c-2.8 0-4.5-2.5-4-5l-1.5-4.5A2 2 0 0 1 6.1 9.4"
        />
      </svg>
    </button>
    <button
      type="button"
      class="ico ico-tool"
      :class="{ on: store.viewportTransformTool === 'rotate' }"
      :aria-pressed="store.viewportTransformTool === 'rotate'"
      :aria-label="store.tUi('floatingTools.rotateAria')"
      :title="store.tUi('floatingTools.rotateTitle')"
      :disabled="!canUseTransformTools"
      @click.stop="store.setViewportTransformTool('rotate')"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
        <ellipse
          cx="12"
          cy="12"
          rx="3.2"
          ry="8.2"
          stroke="currentColor"
          stroke-width="1.55"
          fill="none"
        />
        <path
          stroke="currentColor"
          stroke-width="1.55"
          stroke-linecap="round"
          fill="none"
          d="M5.5 11.5c.5-3.2 3.3-5.5 6.5-5.5 1.2 0 2.3.3 3.2.9M18.5 12.5c-.5 3.2-3.3 5.5-6.5 5.5-1.2 0-2.3-.3-3.2-.9"
        />
        <path
          stroke="currentColor"
          stroke-width="1.55"
          stroke-linecap="round"
          d="M17 6.5l1.2-1.8 1.8 1.2"
        />
      </svg>
    </button>
    <button
      type="button"
      class="ico ico-tool ico-scale"
      :class="{ on: store.viewportTransformTool === 'scale' }"
      :aria-pressed="store.viewportTransformTool === 'scale'"
      :aria-label="store.tUi('floatingTools.scaleAria')"
      :title="store.tUi('floatingTools.scaleTitle')"
      :disabled="!canUseTransformTools"
      @click.stop="store.setViewportTransformTool('scale')"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="5.5" stroke="currentColor" stroke-width="1.55" fill="none" />
        <path
          stroke="currentColor"
          stroke-width="1.55"
          stroke-linecap="round"
          d="M14.2 14.2L19 19M9.2 10h1.6M10 8.8v1.6"
        />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.dock {
  position: absolute;
  left: 50%;
  bottom: 14px;
  z-index: 5;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.45rem;
  border-radius: 14px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  box-shadow: var(--im-shadow);
  pointer-events: auto;
}

.ico-tool {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
}

.ico-tool:hover:not(:disabled) {
  background: var(--im-elevated);
}

.ico-tool:focus-visible {
  outline: none;
}

.ico-tool.on {
  border-color: var(--im-accent);
  background: var(--im-interactive-soft);
}

.ico-tool:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.ico-move:hover:not(:disabled) {
  cursor: grab;
}

.ico-scale:hover:not(:disabled) {
  cursor: ns-resize;
}
</style>
